import { BUTTON_SIZE, BUTTON_TYPE, DSButton, DSModal, useToast } from '@repo/design-system/index'
import { QIcon_book, QIcon_clock, QIcon_tutor_black } from '@repo/icons/index'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { createContext, useContext, useEffect, useState } from 'react'
import PortalContainer from '../../../../../components/container/PortalContainer'
import SelectMaterialBody from '../../../../../components/new-portal/lessons/materials/SelectMaterialBody'
import SelectMaterialSearchSection from '../../../../../components/new-portal/lessons/materials/SelectMaterialSearchSection'
import { useEventTracking } from '../../../../../helpers/gtmHelper'
import apis from '../../../../../modules/apis'
import { axiosGet, axiosPost } from '../../../../../modules/axiosHelper'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../../../modules/hooks/common/useIsWebview'
import {
  n_ai_speaking,
  n_common,
  n_material,
  n_upcoming_lessons,
} from '../../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { upcomingsQueryApi, upcomingsQueryApiV5 } from '../../../../../modules/react-query/queryApis'
import urls from '../../../../../modules/urls'
import { COLORS } from '../../../../../modules/vars'

export const PREV_HOUR_FROM_LESSON = 0
export interface ICourse {
  id: number
  title: string
  subtitle: string
  image_url: string
  category: string
  is_mine: boolean
  is_my_material: boolean
  is_revised: boolean | null
  provide_feedback: boolean
  pdf_link_en: string
  level: number
  course_type: string
  is_today: boolean
}

type SelectMaterialContextType = {
  selectedCourse: ICourse
  setSelectedCourse: (course: any) => void
  keywords: string
  setKeywords: (keywords: any) => void
  lessonId: any
  threadId: any
}

const initialState: SelectMaterialContextType = {
  selectedCourse: {
    id: 0,
    title: '',
    subtitle: '',
    image_url: '',
    category: '',
    is_mine: false,
    is_my_material: false,
    is_revised: false,
    provide_feedback: false,
    pdf_link_en: '',
    level: 0,
    course_type: '',
    is_today: false,
  },
  setSelectedCourse: null,
  keywords: '',
  setKeywords: null,
  lessonId: '',
  threadId: '',
}

export const SelectMaterialContext = createContext(initialState)

interface ISelectMaterialProvider {
  children
  lessonId?
  threadId?
}

export function SelectMaterialProvider({ children, lessonId, threadId }: ISelectMaterialProvider) {
  const [selectedCourse, setSelectedCourse] = useState()
  const [keywords, setKeywords] = useState()

  return (
    <SelectMaterialContext.Provider
      value={{ selectedCourse, setSelectedCourse, keywords, setKeywords, lessonId, threadId }}
    >
      {children}
    </SelectMaterialContext.Provider>
  )
}

const TitleSection = () => {
  const { t } = useTranslation()
  const { query } = useRouter()
  const isChangeMaterial = query.type === 'change'

  const router = useAppRouter()
  const [diffHours, setDiffHours] = useState(10)

  const { data: lessonInfoData, isFetching }: any = useQuery({
    queryKey: ['lesson_data', router.query.lessonId],
    queryFn: () => fetchLessonInfo(router.query.lessonId),
  })

  useEffect(() => {
    if (lessonInfoData && !isFetching) {
      setDiffHours(lessonInfoData?.lesson?.diff_hours)
    }
  }, [lessonInfoData, isFetching])

  return (
    <div className="fixed top-[60px] z-[100] hidden h-[130px] w-screen items-end bg-white pt-[0px] sm:flex">
      <div className="flex w-full flex-row items-center justify-between pr-[32px] pb-[40px] pt-[40px] lg:w-[876px] lg:pr-[0px]">
        <div className="Hero4-32Bd">
          {isChangeMaterial
            ? t(n_upcoming_lessons.edit_materials.change_title)
            : t(n_upcoming_lessons.edit_materials.select_title)}
        </div>
        {diffHours !== PREV_HOUR_FROM_LESSON && <SelectMaterialSearchSection />}
      </div>
    </div>
  )
}

interface BottomFixedSectionProps {
  lessonId?: string | number
  handleClickSaveMaterialWebview?: (lessonId, materalId) => void
  isConversation?: boolean
  isAITutor?: boolean
  loading?: boolean
}

export const BottomFixedSection = ({
  lessonId,
  handleClickSaveMaterialWebview,
  isConversation = false,
  isAITutor = false,
  loading = false,
}: BottomFixedSectionProps) => {
  const router = useAppRouter()
  const { locale, isLocaleKO, t } = useTranslation()
  const { toastError } = useToast()
  const { selectedCourse } = useContext(SelectMaterialContext)
  const isWebview = useIsWebview()
  const queryClient = useQueryClient()
  const fetchLessonInfo = async (lessonId) => {
    const response = await axiosGet(apis.lessons.upcoming(lessonId))

    if (response) return response
  }
  const { data: lessonInfoData, isFetching }: any = useQuery({
    queryKey: ['lesson_data', router.query.lessonId],
    queryFn: () => fetchLessonInfo(router.query.lessonId),
  })

  const handleClickSaveMaterial = async (lessonId, materalId) => {
    try {
      if (!materalId) {
        toastError(t(n_upcoming_lessons.edit_materials.tab_desc))

        return
      }

      const response = await axiosPost(apis.lessons.editCourse(), {
        lesson_id: lessonId,
        course_id: materalId,
      })

      if (response.success) {
        queryClient.invalidateQueries([upcomingsQueryApi.getQueryKey(), upcomingsQueryApiV5.getQueryKey()])
        // [Plus] 교재 변경 성공시 페이지 이동해서 toast 띄우는 로직
        router.push(urls.student.newPortal.lessons.upcomingLessons + '?update=material', {})
      }
    } catch (e) {
      alert(e.message)
    }
  }

  const prevSelectedCourse = lessonInfoData?.data?.course
    ? isLocaleKO
      ? lessonInfoData.data.course.title_kr
      : lessonInfoData.data.course.title
    : ''

  const courseTitle = selectedCourse?.title
    ? selectedCourse?.title
    : prevSelectedCourse && prevSelectedCourse.length > 1
    ? prevSelectedCourse
    : t(n_upcoming_lessons.edit_materials.tab_desc)

  return (
    <div
      className={classNames(
        [isWebview ? 'sticky bottom-[0px] mt-auto' : 'fixed bottom-[0px]'],
        'border-t-1 z-100 left-[0px] flex h-[150px] w-full flex-col items-center border-gray-200 bg-gray-50 py-[12px] sm:h-[80px] sm:border-t-0 sm:py-[11px] lg:ml-[80px] lg:w-[calc(100%_-_80px)]'
      )}
    >
      <div className="mx-auto flex w-full max-w-[1040px] flex-col px-[16px] sm:flex-row sm:justify-between lg:justify-start">
        <div className="flex w-full flex-row">
          <div className="border-r-1 mr-[15px] hidden flex-col gap-[4px] pr-[15px] lg:flex ">
            <div className="hidden min-w-[298px] flex-row gap-[4px] lg:flex">
              <QIcon_clock color={COLORS.GRAY500} />
              <div className="Body2-14Md">{t(n_upcoming_lessons.edit_materials.tab_time)}</div>
            </div>
            <div className="Title2-18Bd">{lessonInfoData?.lesson?.start_time}</div>
          </div>
          <div className="border-r-1 mr-[15px] hidden min-w-[298px] flex-col gap-[4px] pr-[15px] lg:flex">
            <div className="flex flex-row gap-[4px]">
              <QIcon_tutor_black color={COLORS.GRAY500} />
              <div className="Body2-14Md">{t(n_upcoming_lessons.edit_materials.tab_tutor)}</div>
            </div>
            <div className="Title2-18Bd">{lessonInfoData?.tutor?.tutor_name}</div>
          </div>
          <div className="hidden w-full flex-row items-center gap-[8px] sm:h-auto sm:flex-col sm:gap-[4px] lg:flex lg:min-w-[298px]">
            <div className="items-cetner flex min-w-[0px] flex-row gap-[4px] whitespace-nowrap sm:w-full sm:min-w-[268px]">
              <QIcon_book color={COLORS.GRAY500} />
              <div className="Body2-14Md">{t(n_upcoming_lessons.edit_materials.tab_materials)}</div>
            </div>
            <div className="Body2-14Md sm:Title2-18Md mb-[2px] flex w-full items-center justify-start overflow-hidden text-purple-500">
              <div className="max-w-[268px] truncate break-keep">{courseTitle}</div>
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-[8px] lg:hidden">
          <div className="Body2-14Bd text-purple-500">{t(n_material.select)}</div>
          <div className="Body2-14Md text-gray-700">
            {courseTitle ? courseTitle : t(n_upcoming_lessons.edit_materials.tab_desc)}
          </div>
        </div>
        <div className="mt-[24px] w-full sm:mt-[0px] sm:max-w-[207px]">
          <DSButton
            isLoading={loading}
            disabled={!selectedCourse?.id}
            type={BUTTON_TYPE.SOLID}
            size={BUTTON_SIZE.LG}
            onClick={() =>
              handleClickSaveMaterialWebview
                ? handleClickSaveMaterialWebview(
                    lessonId ? lessonId : lessonInfoData?.data?.lesson?.id,
                    selectedCourse?.id
                  )
                : handleClickSaveMaterial(lessonId ? lessonId : router.query.lessonId, selectedCourse?.id)
            }
          >
            <DSButton.Label className="Body1-16Bd whitespace-nowrap">
              {isConversation || isAITutor
                ? t(n_ai_speaking.material.btn_start)
                : t(n_upcoming_lessons.edit_materials.tab_btn_done)}
            </DSButton.Label>
          </DSButton>
        </div>
      </div>
    </div>
  )
}

const fetchLessonInfo = async (lessonId) => {
  const response = await axiosGet(apis.lessons.upcoming(lessonId))

  if (response) return response.data
}

export const SelectMaterial = ({ isConversation = false }) => {
  const isWebview = useIsWebview()

  return (
    <>
      <div className="h-full sm:mt-[40px] sm:px-0 lg:mt-[100px] lg:w-[876px]">
        <TitleSection />
        <SelectMaterialBody isConversation={isConversation} />
        {!isWebview && <BottomFixedSection />}
      </div>
    </>
  )
}

const Index = () => {
  const { t } = useTranslation()
  const [showBackPopup, setShowBackPopup] = useState<boolean>(false)
  const { sendPageViewEvent, sendClickEvent } = useEventTracking()
  const router = useAppRouter()

  useEffect(() => {
    if (!router) return
    if (router.query.type === 'change') {
      sendPageViewEvent({ page_name: 'lesson_change_material' })
    } else {
      sendPageViewEvent({ page_name: 'lesson_select_material' })
    }
  }, [router.asPath])

  const BackPopup = () => {
    const isChangeMaterial = router.query.type === 'change'

    return (
      <DSModal
        id="TextOnlySMTwoButtonHorizontalPopup"
        isOpen={showBackPopup}
        setIsOpen={setShowBackPopup}
        size="lg"
        blockAddHistory={true}
      >
        <DSModal.Title>
          {isChangeMaterial
            ? t(n_upcoming_lessons.edit_materials.popuop_title2)
            : t(n_upcoming_lessons.edit_materials.popuop_title1)}
        </DSModal.Title>
        <DSModal.ButtonContainer direction="horizontal">
          <DSButton
            size={BUTTON_SIZE.MD}
            type={BUTTON_TYPE.OUTLINE}
            customBgColor={COLORS.WHITE}
            onClick={(e) => setShowBackPopup(false)}
          >
            <DSButton.Label>{t(n_common.btn_continue)}</DSButton.Label>
          </DSButton>
          <DSButton
            size={BUTTON_SIZE.MD}
            type={BUTTON_TYPE.SOLID}
            className="text-white"
            onClick={() => router.push(urls.student.newPortal.lessons.upcomingLessons)}
          >
            <DSButton.Label>{t(n_common.btn_leave)}</DSButton.Label>
          </DSButton>
        </DSModal.ButtonContainer>
      </DSModal>
    )
  }

  return (
    <PortalContainer
      layoutClassName="mx-auto mb-[120px] h-full w-full max-w-[1040px] px-[16px] overflow-x-hidden"
      onBackClick={() => {
        sendClickEvent({ button_name: 'back' })
        setShowBackPopup(true)
      }}
    >
      <SelectMaterialProvider>
        <div className="mt-[59px]">
          <SelectMaterial />
        </div>
      </SelectMaterialProvider>
      <BackPopup />
    </PortalContainer>
  )
}
export default Index
